/**
 * The one piece of sign-in state that must survive a reload: which address a
 * code was just requested for, and when.
 *
 * sessionStorage, not localStorage, and not React state alone. iOS discards
 * backgrounded tabs freely, and a rep switching to Mail to read the code is
 * exactly the moment it happens. The tab reloads on the same route (the deep
 * link is still in the address bar) and the code step comes back from here.
 * sessionStorage is scoped to this tab, which is the only tab the code is any
 * use in, and it does not outlive it.
 *
 * Every access is wrapped. Storage can throw (private mode, blocked site data,
 * quota) and a failure here must only ever mean "show the email step again",
 * never a broken sign-in screen.
 */

const KEY = 'esl.pendingCode'

/** Must match `mailer_otp_length` in the project's auth config. */
export const CODE_LENGTH = 6
/** Must match `mailer_otp_exp` (600 s) in the project's auth config. Used only
 *  to word an error: Supabase returns the same 403 for wrong and expired. */
export const CODE_TTL_MS = 10 * 60 * 1000
/** Client-side, and identical for every address. otp-request's own per-address
 *  throttle is silent by design; this keeps the button honest without
 *  revealing which addresses that throttle applies to.
 *
 *  65 s, not 60: the server's per-address window (60 s, and Auth's
 *  `smtp_max_frequency`) runs from when it logs the send, but `requestedAt`
 *  is taken before the request leaves the browser. At exactly 60 s a resend
 *  could land just inside the window and be throttled silently. The extra 5 s
 *  absorbs that request latency. */
export const RESEND_COOLDOWN_MS = 65 * 1000

export interface PendingCode {
  /** Normalized the same way otp-request normalizes it: trim + lowercase. */
  email: string
  /** Epoch ms of the most recent request for this address, from this tab. */
  requestedAt: number
}

export function readPendingCode(): PendingCode | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as PendingCode).email === 'string' &&
      (parsed as PendingCode).email.length > 0 &&
      typeof (parsed as PendingCode).requestedAt === 'number' &&
      Number.isFinite((parsed as PendingCode).requestedAt)
    ) {
      return { email: (parsed as PendingCode).email, requestedAt: (parsed as PendingCode).requestedAt }
    }
    return null
  } catch {
    return null
  }
}

export function writePendingCode(pending: PendingCode): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(pending))
  } catch {
    // The code step still works for this page load; it just will not survive
    // a reload. Nothing better is available.
  }
}

export function clearPendingCode(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // Nothing to do.
  }
}
